import {DesignerVersion} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignState';
import {IMigration} from '@mintdata-wsp/wsp/controllers/visualDesigner/designState/DesignMigrationController';
import {Assert} from '@mintdata/mint-kit/impl/util/Assert';

const MIGRATED_VERSION:DesignerVersion = 37;

const componentsForMigration = [
    'Label',
    'Box',
    'Button',
    'Image',
    'TextInput',
    'DateInput',
    'NumericInput',
    'TimeInput',
    'TextArea',
    'RichTextEditor'
];

export const migration_36_37:IMigration = {
    from: MIGRATED_VERSION - 1,
    to: MIGRATED_VERSION,
    migrate: (designState:any) =>
    {
        Assert.yes(designState.designerVersion === MIGRATED_VERSION - 1);

        console.log(`migration ${MIGRATED_VERSION - 1}->${MIGRATED_VERSION} designState`, designState);

        if( designState.surface && designState.surface.pages )
        {
            Object.values(designState.surface.pages).forEach((page:any) =>
            {
                if( page.artboard && page.artboard.allBoxes )
                {
                    Object.values(page.artboard.allBoxes).forEach((boxSnapshot:any) =>
                    {
                        const {component} = boxSnapshot;

                        if( componentsForMigration.indexOf(component.name) !== -1 )
                        {
                            const {componentDesc} = boxSnapshot;

                            Object.values(componentDesc._properties).forEach((propSnapshot:any) =>
                            {
                                if( propSnapshot.name === 'model' )
                                    setComponentProps(component.name, propSnapshot, designState.bindings);
                            });
                        }
                    });
                }
            });
        }

        designState.designerVersion = MIGRATED_VERSION;

        return designState;
    }
};

function setComponentProps(componentName:string, propSnapshot:any, bindings:any)
{
    const {_value, bindingIds} = propSnapshot;

    if( componentName !== 'Box' )
        moveBgColorToBackground(_value, bindingIds, bindings);

    if( componentName === 'Box' )
        addSomeBoxProps(_value);

    if( ['Label', 'Image'].indexOf(componentName) !== -1 )
        addBoxShadow(_value);

    if( ['Label', 'Box', 'Image'].indexOf(componentName) !== -1 )
        moveBorderFieldsToBorder(_value, bindingIds, bindings);

    addHoverState(_value, componentName);

    if( ['Label', 'Box', 'Button', 'Image'].indexOf(componentName) !== -1 )
        addActiveState(_value, componentName);
    // 'TextInput', 'DateInput', 'NumericInput', 'TimeInput', 'TextArea', 'RichTextEditor',
    else
        addFocusState(_value, componentName);
}

function moveBgColorToBackground(value:any, propBindingIds:string[], bindings:any)
{
    let solidColor = {
        'color': {
            'hex': {
                '__type': 'STRING',
                'value': '#ffffff'
            },
            'alpha': {
                '__type': 'NUMBER',
                'value': 1
            }
        },
        'visibility': {
            '__type': 'BOOLEAN',
            'value': false
        }
    };

    if( value.bgColor ) solidColor = value.bgColor;

    value.background = {
        'type': {
            '__type': 'STRINGENUM',
            'selectedValue': 'Solid Color',
            'values': [
                'Solid Color',
                'Linear Gradient'
            ]
        },
        'solidColor': solidColor,
        'gradientFromColor': {
            'color': {
                'hex': {
                    '__type': 'STRING',
                    'value': '#ffffff'
                },
                'alpha': {
                    '__type': 'NUMBER',
                    'value': 1
                }
            },
            'visibility': {
                '__type': 'BOOLEAN',
                'value': true
            }
        },
        'gradientToColor': {
            'color': {
                'hex': {
                    '__type': 'STRING',
                    'value': '#000000'
                },
                'alpha': {
                    '__type': 'NUMBER',
                    'value': 1
                }
            },
            'visibility': {
                '__type': 'BOOLEAN',
                'value': true
            }
        },
        'gradientAngle': {
            '__type': 'NUMBER',
            'value': 0
        }
    };

    if( value.bgColor ) delete value.bgColor;

    // -- for Image Component
    if( value.backgroundColor && value.backgroundVisibility )
    {
        value.background.solidColor.color = value.backgroundColor;
        value.background.solidColor.visibility = value.backgroundVisibility;

        delete value.backgroundColor;
        delete value.backgroundVisibility;
    }

    // -- for Button Component
    if( value.bgGradient )
    {
        value.background.gradientFromColor = value.bgGradient.gradientFromColor;
        value.background.gradientToColor = value.bgGradient.gradientToColor;
        value.background.gradientAngle = value.bgGradient.gradientAngle;

        delete value.bgGradient;
    }

    // handle bindings
    propBindingIds.forEach((bindingId:string) =>
    {
        const binding:any = bindings[bindingId];

        if( !binding )
            return;

        if( binding.propPath.startsWith('bgColor') )
            binding.propPath = binding.propPath.replace('bgColor', 'background.solidColor');
        // -- for Image Component
        else if( binding.propPath.startsWith('backgroundColor') )
            binding.propPath = binding.propPath.replace('backgroundColor', 'background.solidColor.color');
        else if( binding.propPath.startsWith('backgroundVisibility') )
            binding.propPath = binding.propPath.replace('backgroundVisibility', 'background.solidColor.visibility');
        // -- for Button Component
        else if( binding.propPath.startsWith('bgGradient') )
            binding.propPath = binding.propPath.replace('bgGradient', 'background');
    });
}

function moveBorderFieldsToBorder(value:any, propBindingIds:string[], bindings:any)
{
    value.border = {
        'borderColor': value.borderColor,
        'borderThickness': value.borderThickness,
        'borderStyle': value.borderStyle,
        'borderRadius': value.borderRadius,
        'borderFill': {
            '__type': 'STRINGENUM',
            'selectedValue': 'borderAll',
            'values': [
                'borderLeft',
                'borderTop',
                'borderRight',
                'borderBottom',
                'borderAll'
            ]
        }
    };

    delete value.borderColor;
    delete value.borderThickness;
    delete value.borderStyle;
    delete value.borderRadius;

    // -- for Image Component
    if( value.borderVisibility )
    {
        value.border.borderColor = {
            color: value.border.borderColor,
            visibility: value.borderVisibility
        };

        delete value.borderVisibility;
    }

    // handle bindings
    propBindingIds.forEach((bindingId:string) =>
    {
        const binding:any = bindings[bindingId];

        if( !binding )
            return;

        if( binding.propPath.startsWith('borderColor') )
            binding.propPath = binding.propPath.replace('borderColor', 'border.borderColor');
        else if( binding.propPath.startsWith('borderThickness') )
            binding.propPath = binding.propPath.replace('borderThickness', 'border.borderThickness');
        else if( binding.propPath.startsWith('borderStyle') )
            binding.propPath = binding.propPath.replace('borderStyle', 'border.borderStyle');
        else if( binding.propPath.startsWith('borderRadius') )
            binding.propPath = binding.propPath.replace('borderRadius', 'border.borderRadius');
        // -- for Image Component
        else if( binding.propPath.startsWith('borderVisibility') )
            binding.propPath = binding.propPath.replace('borderVisibility', 'border.borderColor.visibility');
    });
}

function addBoxShadow(value:any)
{
    value.boxShadow = {
        'color': {
            'color': {
                'hex': {
                    '__type': 'STRING',
                    'value': '#000000'
                },
                'alpha': {
                    '__type': 'NUMBER',
                    'value': 0.5
                }
            },
            'visibility': {
                '__type': 'BOOLEAN',
                'value': false
            }
        },
        'deltaX': {
            'value': {
                '__type': 'NUMBER',
                'value': 0
            },
            'unitsType': {
                '__type': 'STRINGENUM',
                'selectedValue': 'px',
                'values': [
                    'px',
                    '%',
                    'auto'
                ]
            }
        },
        'deltaY': {
            'value': {
                '__type': 'NUMBER',
                'value': 0
            },
            'unitsType': {
                '__type': 'STRINGENUM',
                'selectedValue': 'px',
                'values': [
                    'px',
                    '%',
                    'auto'
                ]
            }
        },
        'blur': {
            'value': {
                '__type': 'NUMBER',
                'value': 0
            },
            'unitsType': {
                '__type': 'STRINGENUM',
                'selectedValue': 'px',
                'values': [
                    'px',
                    '%',
                    'auto'
                ]
            }
        },
        'spread': {
            'value': {
                '__type': 'NUMBER',
                'value': 0
            },
            'unitsType': {
                '__type': 'STRINGENUM',
                'selectedValue': 'px',
                'values': [
                    'px',
                    '%',
                    'auto'
                ]
            }
        }
    };
}

function addSomeBoxProps(value:any)
{
    value.isHoverEventPropagationEnabled = {
        '__type': 'BOOLEAN',
        'value': false
    };
    value.hoverEventPropagation = {
        '__type': 'STRINGENUM',
        'selectedValue': 'dispatch itself',
        'values': [
            'propagate from parent',
            'dispatch itself'
        ]
    };
    value.isActiveEventPropagationEnabled = {
        '__type': 'BOOLEAN',
        'value': false
    };
}

function addHoverState(value:any, componentName:string)
{
    value.hoverState = newComponentState(componentName);
}

function addActiveState(value:any, componentName:string)
{
    value.activeState = newComponentState(componentName);
}

function addFocusState(value:any, componentName:string)
{
    value.focusState = newComponentState(componentName);
}

function newComponentState(componentName:string):object
{
    const state = newNullState();

    state.background = backgroundDefault();
    state.boxShadow = boxShadowDefault();

    switch( componentName )
    {
        case 'Label':

            state.font = fontDefault();
            state.background = backgroundForLabel();
            state.border = borderDefault();
            state.boxShadow = boxShadowNone();
            break;

        case 'Box':

            state.border = borderForBox();
            break;

        case 'Button':

            state.isEnabled.value = true;
            state.font = fontDefault();
            state.border = borderForInput();
            break;

        case 'Image':

            state.boxShadow = boxShadowNone();
            state.border = borderDefault();
            break;

        case 'TextInput':
        case 'DateInput':
        case 'TimeInput':
        case 'NumericInput':
        case 'TextArea':
        case 'RichTextEditor':

            state.border = borderForInput();
            break;
    }

    return state;
}

function newNullState()
{
    return {
        'isEnabled': {
            '__type': 'BOOLEAN',
            'value': false
        },
        'font': null,
        'background': null,
        'border': null,
        'boxShadow': null
    };
}

function backgroundDefault()
{
    return {
        'type': {
            '__type': 'STRINGENUM',
            'selectedValue': 'Solid Color',
            'values': [
                'Solid Color',
                'Linear Gradient'
            ]
        },
        'solidColor': {
            'color': {
                'hex': {
                    '__type': 'STRING',
                    'value': '#ffffff'
                },
                'alpha': {
                    '__type': 'NUMBER',
                    'value': 1
                }
            },
            'visibility': {
                '__type': 'BOOLEAN',
                'value': true
            }
        },
        'gradientFromColor': {
            'color': {
                'hex': {
                    '__type': 'STRING',
                    'value': '#ffffff'
                },
                'alpha': {
                    '__type': 'NUMBER',
                    'value': 1
                }
            },
            'visibility': {
                '__type': 'BOOLEAN',
                'value': true
            }
        },
        'gradientToColor': {
            'color': {
                'hex': {
                    '__type': 'STRING',
                    'value': '#000000'
                },
                'alpha': {
                    '__type': 'NUMBER',
                    'value': 1
                }
            },
            'visibility': {
                '__type': 'BOOLEAN',
                'value': true
            }
        },
        'gradientAngle': {
            '__type': 'NUMBER',
            'value': 0
        }
    };
}

function backgroundForLabel()
{
    return {
        'type': {
            '__type': 'STRINGENUM',
            'selectedValue': 'Solid Color',
            'values': [
                'Solid Color',
                'Linear Gradient'
            ]
        },
        'solidColor': {
            'color': {
                'hex': {
                    '__type': 'STRING',
                    'value': '#ffffff'
                },
                'alpha': {
                    '__type': 'NUMBER',
                    'value': 1
                }
            },
            'visibility': {
                '__type': 'BOOLEAN',
                'value': false
            }
        },
        'gradientFromColor': {
            'color': {
                'hex': {
                    '__type': 'STRING',
                    'value': '#ffffff'
                },
                'alpha': {
                    '__type': 'NUMBER',
                    'value': 1
                }
            },
            'visibility': {
                '__type': 'BOOLEAN',
                'value': true
            }
        },
        'gradientToColor': {
            'color': {
                'hex': {
                    '__type': 'STRING',
                    'value': '#000000'
                },
                'alpha': {
                    '__type': 'NUMBER',
                    'value': 1
                }
            },
            'visibility': {
                '__type': 'BOOLEAN',
                'value': true
            }
        },
        'gradientAngle': {
            '__type': 'NUMBER',
            'value': 0
        }
    };
}

function boxShadowDefault()
{
    return {
        'color': {
            'color': {
                'hex': {
                    '__type': 'STRING',
                    'value': '#000000'
                },
                'alpha': {
                    '__type': 'NUMBER',
                    'value': 0.5
                }
            },
            'visibility': {
                '__type': 'BOOLEAN',
                'value': false
            }
        },
        'deltaX': {
            'value': {
                '__type': 'NUMBER',
                'value': 0
            },
            'unitsType': {
                '__type': 'STRINGENUM',
                'selectedValue': 'px',
                'values': [
                    'px',
                    '%',
                    'auto'
                ]
            }
        },
        'deltaY': {
            'value': {
                '__type': 'NUMBER',
                'value': 2
            },
            'unitsType': {
                '__type': 'STRINGENUM',
                'selectedValue': 'px',
                'values': [
                    'px',
                    '%',
                    'auto'
                ]
            }
        },
        'blur': {
            'value': {
                '__type': 'NUMBER',
                'value': 4
            },
            'unitsType': {
                '__type': 'STRINGENUM',
                'selectedValue': 'px',
                'values': [
                    'px',
                    '%',
                    'auto'
                ]
            }
        },
        'spread': {
            'value': {
                '__type': 'NUMBER',
                'value': 0
            },
            'unitsType': {
                '__type': 'STRINGENUM',
                'selectedValue': 'px',
                'values': [
                    'px',
                    '%',
                    'auto'
                ]
            }
        }
    };
}

function borderDefault()
{
    return {
        'borderColor': {
            'color': {
                'hex': {
                    '__type': 'STRING',
                    'value': '#000000'
                },
                'alpha': {
                    '__type': 'NUMBER',
                    'value': 1
                }
            },
            'visibility': {
                '__type': 'BOOLEAN',
                'value': false
            }
        },
        'borderThickness': {
            'value': {
                '__type': 'NUMBER',
                'value': 1
            },
            'unitsType': {
                '__type': 'STRINGENUM',
                'selectedValue': 'px',
                'values': [
                    'px',
                    '%',
                    'auto'
                ]
            }
        },
        'borderStyle': {
            '__type': 'STRINGENUM',
            'selectedValue': 'solid',
            'values': [
                'solid',
                'dashed',
                'dotted'
            ]
        },
        'borderRadius': {
            'topLeft': {
                'value': {
                    '__type': 'NUMBER',
                    'value': 0
                },
                'unitsType': {
                    '__type': 'STRINGENUM',
                    'selectedValue': 'px',
                    'values': [
                        'px',
                        '%',
                        'auto'
                    ]
                }
            },
            'topRight': {
                'value': {
                    '__type': 'NUMBER',
                    'value': 0
                },
                'unitsType': {
                    '__type': 'STRINGENUM',
                    'selectedValue': 'px',
                    'values': [
                        'px',
                        '%',
                        'auto'
                    ]
                }
            },
            'bottomLeft': {
                'value': {
                    '__type': 'NUMBER',
                    'value': 0
                },
                'unitsType': {
                    '__type': 'STRINGENUM',
                    'selectedValue': 'px',
                    'values': [
                        'px',
                        '%',
                        'auto'
                    ]
                }
            },
            'bottomRight': {
                'value': {
                    '__type': 'NUMBER',
                    'value': 0
                },
                'unitsType': {
                    '__type': 'STRINGENUM',
                    'selectedValue': 'px',
                    'values': [
                        'px',
                        '%',
                        'auto'
                    ]
                }
            }
        },
        'borderFill': {
            '__type': 'STRINGENUM',
            'selectedValue': 'borderAll',
            'values': [
                'borderLeft',
                'borderTop',
                'borderRight',
                'borderBottom',
                'borderAll'
            ]
        }
    };
}

function borderForInput()
{
    return {
        'borderColor': {
            'color': {
                'hex': {
                    '__type': 'STRING',
                    'value': '#cdced0'
                },
                'alpha': {
                    '__type': 'NUMBER',
                    'value': 1
                }
            },
            'visibility': {
                '__type': 'BOOLEAN',
                'value': true
            }
        },
        'borderThickness': {
            'value': {
                '__type': 'NUMBER',
                'value': 1
            },
            'unitsType': {
                '__type': 'STRINGENUM',
                'selectedValue': 'px',
                'values': [
                    'px',
                    '%',
                    'auto'
                ]
            }
        },
        'borderStyle': {
            '__type': 'STRINGENUM',
            'selectedValue': 'solid',
            'values': [
                'solid',
                'dashed',
                'dotted'
            ]
        },
        'borderRadius': {
            'topLeft': {
                'value': {
                    '__type': 'NUMBER',
                    'value': 3
                },
                'unitsType': {
                    '__type': 'STRINGENUM',
                    'selectedValue': 'px',
                    'values': [
                        'px',
                        '%',
                        'auto'
                    ]
                }
            },
            'topRight': {
                'value': {
                    '__type': 'NUMBER',
                    'value': 3
                },
                'unitsType': {
                    '__type': 'STRINGENUM',
                    'selectedValue': 'px',
                    'values': [
                        'px',
                        '%',
                        'auto'
                    ]
                }
            },
            'bottomLeft': {
                'value': {
                    '__type': 'NUMBER',
                    'value': 3
                },
                'unitsType': {
                    '__type': 'STRINGENUM',
                    'selectedValue': 'px',
                    'values': [
                        'px',
                        '%',
                        'auto'
                    ]
                }
            },
            'bottomRight': {
                'value': {
                    '__type': 'NUMBER',
                    'value': 3
                },
                'unitsType': {
                    '__type': 'STRINGENUM',
                    'selectedValue': 'px',
                    'values': [
                        'px',
                        '%',
                        'auto'
                    ]
                }
            }
        },
        'borderFill': {
            '__type': 'STRINGENUM',
            'selectedValue': 'borderAll',
            'values': [
                'borderLeft',
                'borderTop',
                'borderRight',
                'borderBottom',
                'borderAll'
            ]
        }
    };
}

function borderForBox()
{
    return {
        'borderColor': {
            'color': {
                'hex': {
                    '__type': 'STRING',
                    'value': '#000000'
                },
                'alpha': {
                    '__type': 'NUMBER',
                    'value': 1
                }
            },
            'visibility': {
                '__type': 'BOOLEAN',
                'value': true
            }
        },
        'borderThickness': {
            'value': {
                '__type': 'NUMBER',
                'value': 1
            },
            'unitsType': {
                '__type': 'STRINGENUM',
                'selectedValue': 'px',
                'values': [
                    'px',
                    '%',
                    'auto'
                ]
            }
        },
        'borderStyle': {
            '__type': 'STRINGENUM',
            'selectedValue': 'solid',
            'values': [
                'solid',
                'dashed',
                'dotted'
            ]
        },
        'borderRadius': {
            'topLeft': {
                'value': {
                    '__type': 'NUMBER',
                    'value': 0
                },
                'unitsType': {
                    '__type': 'STRINGENUM',
                    'selectedValue': 'px',
                    'values': [
                        'px',
                        '%',
                        'auto'
                    ]
                }
            },
            'topRight': {
                'value': {
                    '__type': 'NUMBER',
                    'value': 0
                },
                'unitsType': {
                    '__type': 'STRINGENUM',
                    'selectedValue': 'px',
                    'values': [
                        'px',
                        '%',
                        'auto'
                    ]
                }
            },
            'bottomLeft': {
                'value': {
                    '__type': 'NUMBER',
                    'value': 0
                },
                'unitsType': {
                    '__type': 'STRINGENUM',
                    'selectedValue': 'px',
                    'values': [
                        'px',
                        '%',
                        'auto'
                    ]
                }
            },
            'bottomRight': {
                'value': {
                    '__type': 'NUMBER',
                    'value': 0
                },
                'unitsType': {
                    '__type': 'STRINGENUM',
                    'selectedValue': 'px',
                    'values': [
                        'px',
                        '%',
                        'auto'
                    ]
                }
            }
        },
        'borderFill': {
            '__type': 'STRINGENUM',
            'selectedValue': 'borderAll',
            'values': [
                'borderLeft',
                'borderTop',
                'borderRight',
                'borderBottom',
                'borderAll'
            ]
        }
    };
}

function boxShadowNone()
{
    return {
        'color': {
            'color': {
                'hex': {
                    '__type': 'STRING',
                    'value': '#000000'
                },
                'alpha': {
                    '__type': 'NUMBER',
                    'value': 0.5
                }
            },
            'visibility': {
                '__type': 'BOOLEAN',
                'value': false
            }
        },
        'deltaX': {
            'value': {
                '__type': 'NUMBER',
                'value': 0
            },
            'unitsType': {
                '__type': 'STRINGENUM',
                'selectedValue': 'px',
                'values': [
                    'px',
                    '%',
                    'auto'
                ]
            }
        },
        'deltaY': {
            'value': {
                '__type': 'NUMBER',
                'value': 0
            },
            'unitsType': {
                '__type': 'STRINGENUM',
                'selectedValue': 'px',
                'values': [
                    'px',
                    '%',
                    'auto'
                ]
            }
        },
        'blur': {
            'value': {
                '__type': 'NUMBER',
                'value': 0
            },
            'unitsType': {
                '__type': 'STRINGENUM',
                'selectedValue': 'px',
                'values': [
                    'px',
                    '%',
                    'auto'
                ]
            }
        },
        'spread': {
            'value': {
                '__type': 'NUMBER',
                'value': 0
            },
            'unitsType': {
                '__type': 'STRINGENUM',
                'selectedValue': 'px',
                'values': [
                    'px',
                    '%',
                    'auto'
                ]
            }
        }
    };
}

function fontDefault()
{
    return {
        'typeface': {
            '__type': 'STRINGENUM',
            'selectedValue': 'Helvetica Neue',
            'values': [
                'Arial',
                'Verdana',
                'Times New Roman',
                'Helvetica Neue'
            ]
        },
        'weight': {
            '__type': 'STRINGENUM',
            'selectedValue': 'Regular',
            'values': [
                'Thin',
                'Light',
                'Regular',
                'Medium',
                'Bold'
            ]
        },
        'color': {
            'hex': {
                '__type': 'STRING',
                'value': '#000000'
            },
            'alpha': {
                '__type': 'NUMBER',
                'value': 1
            }
        },
        'size': {
            'value': {
                '__type': 'NUMBER',
                'value': 16
            },
            'unitsType': {
                '__type': 'STRINGENUM',
                'selectedValue': 'px',
                'values': [
                    'px',
                    '%',
                    'auto'
                ]
            }
        },
        'lineHeight': {
            'value': {
                '__type': 'NUMBER',
                'value': 18
            },
            'unitsType': {
                '__type': 'STRINGENUM',
                'selectedValue': 'px',
                'values': [
                    'px',
                    '%',
                    'auto'
                ]
            }
        },
        'character': {
            'value': {
                '__type': 'NUMBER',
                'value': 1
            },
            'unitsType': {
                '__type': 'STRINGENUM',
                'selectedValue': 'px',
                'values': [
                    'px',
                    '%',
                    'auto'
                ]
            }
        },
        'fontStyle': {
            '__type': 'BOOLEAN',
            'value': false
        },
        'decorationUnderline': {
            '__type': 'BOOLEAN',
            'value': false
        },
        'decorationCrossed': {
            '__type': 'BOOLEAN',
            'value': false
        }
    };
}
